import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { IntegrationsProvider } from 'contexts/IntegrationsContext'
import { IMainState } from 'store/states'
import { BlockTypes } from 'enum/block-type.enum'
import { IEvaluationResume } from 'models/evaluation.model'
import {
  BlockSurveyContent,
  BlockTaskContent,
} from 'store/states/lessons.state'
import { blockHasResponse } from 'helpers/validators.helper'
import { mandatoryEvaluationBlocksAdvance } from 'helpers/evaluations/mandatoryEvaluationProgress'
import { CourseStateType } from 'enum'
import { useMultimediaProgress, useIsLastLessonHigher } from './hooks'

interface ViewContextProps {
  multimediaProgress: {
    finished: boolean
  }
  isLastLessonHigher: boolean
  canAnswer: boolean
  previewMode: boolean
  enrolled: boolean
  imported: boolean
  mandatoryTaskUploadInProgress: boolean
  setMandatoryTaskUploadInProgress: (inProgress: boolean) => void
  validations: {
    lesson: {
      everyHasResponse: boolean
      everyEvaluationsHasResponse: boolean
      everyTasksHasResponse: boolean
    }
  }
}

export const CourseContext = createContext<ViewContextProps>(
  Object.create(null)
)

export const CourseContextConsumer = CourseContext.Consumer

export const useCourse = () => {
  const context = useContext(CourseContext)
  if (context === undefined) {
    throw new Error('useCourseContext must be used within a CourseProvider')
  }
  return context
}

export const CourseProvider: React.FC<{ previewMode?: boolean }> = ({
  children,
  previewMode = false,
}) => {
  const multimediaProgress = useMultimediaProgress()
  const isLastLessonHigher = useIsLastLessonHigher()
  const enrol = useSelector(({ courses }: IMainState) => courses.course?.enrol)
  const finishDate = enrol?.finishDate

  const courseState = useSelector(
    ({ courses }: IMainState) => courses.course?.state
  )

  const isImparted = courseState === CourseStateType.Imparted
  const isInactive = courseState === CourseStateType.Inactive

  const {
    currentLessonContent,
    loading: lessonContentLoading,
    loadFailed: lessonLoadFailed,
  } = useSelector(({ lmsLessons }: IMainState) => lmsLessons)

  // Solo evaluaciones v2 (Evaluation): no considerar BlockEvaluation (v1) para validar contenido pendiente
  const blockEvaluations = useMemo(() => {
    if (!currentLessonContent || !Array.isArray(currentLessonContent)) {
      return []
    }
    return currentLessonContent.filter(
      (block) => block && block.type === BlockTypes.Evaluation
    ) as IEvaluationResume[]
  }, [currentLessonContent])

  const blockTasks = useMemo(() => {
    if (!currentLessonContent || !Array.isArray(currentLessonContent)) {
      return []
    }
    return currentLessonContent.filter(
      (block) => block && block.type === BlockTypes.BlockTask
    ) as BlockTaskContent[]
  }, [currentLessonContent])

  const blockSurveys = useMemo(() => {
    if (!currentLessonContent || !Array.isArray(currentLessonContent)) {
      return []
    }
    return currentLessonContent.filter(
      (block) => block && block.type === BlockTypes.BlockSurvey
    ) as BlockSurveyContent[]
  }, [currentLessonContent])

  // La lección actual tiene todas las evaluaciones obligatorias v2 completadas.
  // Las evaluaciones opcionales no bloquean el avance ni la finalización (misma lógica que el botón deshabilitado).
  const everyEvaluationsHasResponse = useMemo(() => {
    if (lessonContentLoading) {
      return false
    }
    return blockEvaluations.every((block: IEvaluationResume) => {
      if (!block.required) {
        return true
      }
      return !mandatoryEvaluationBlocksAdvance(block)
    })
  }, [blockEvaluations, lessonContentLoading])

  // La lección actual tiene todos las tareas obligatorios completados
  const everyTasksHasResponse = useMemo(() => {
    if (lessonContentLoading) {
      return false
    }
    return blockTasks
      .filter((task) => task.mandatory)
      .every((block) => blockHasResponse(block))
  }, [blockTasks, lessonContentLoading])

  // La lección actual tiene todos las encuestas obligatorios completados
  const everySurveysHasResponse = useMemo(
    () => blockSurveys.every((block) => blockHasResponse(block)),
    [blockSurveys]
  )

  // Empty content after a failed load says nothing about the lesson's mandatory blocks
  const everyHasResponse =
    !lessonLoadFailed &&
    everyEvaluationsHasResponse &&
    everyTasksHasResponse &&
    everySurveysHasResponse

  const [mandatoryTaskUploadInProgress, setMandatoryTaskUploadInProgressState] =
    useState(false)

  const setMandatoryTaskUploadInProgress = useCallback(
    (inProgress: boolean) => {
      setMandatoryTaskUploadInProgressState(inProgress)
    },
    []
  )

  return (
    <CourseContext.Provider
      value={{
        multimediaProgress,
        isLastLessonHigher,
        // Si no tiene fecha de finalización puede responder y si el curso no está impartido
        canAnswer: !finishDate && !isImparted && !isInactive,
        previewMode,
        enrolled: !!enrol?.id,
        imported: !!enrol?.imported,
        mandatoryTaskUploadInProgress,
        setMandatoryTaskUploadInProgress,
        validations: {
          lesson: {
            everyHasResponse,
            everyEvaluationsHasResponse,
            everyTasksHasResponse,
          },
        },
      }}
    >
      <IntegrationsProvider>{children}</IntegrationsProvider>
    </CourseContext.Provider>
  )
}

export * from './hooks'
